import useGraphQLClient from './graphqlClient';
import {
  SortOrder,
  FavoriteProductions,
  FavoriteProductionsCreateInput,
  FavoriteProductionsWhereUniqueInput,
  FavoriteProductionsOrderByWithRelationInput,
  FavoriteProductionsWhereInput,
} from './spotwrite';

/**
 * 作品の検索条件
 */
export type FavoriteProductionsSearchCondition = {
  userId?: number;
  genreId?: number;
  formId?: number;
  seriesId?: number;
  limit?: number;
  offset?: number;
  orderBy?: FavoriteProductionsOrderByWithRelationInput[];
};

class FavoriteService {
  /**
   * 検索条件を整形します
   * @param condition
   * @returns
   */
  private buildSearchWhere = (condition: FavoriteProductionsSearchCondition) => {
    const andWhere: FavoriteProductionsWhereInput[] = [];

    // ユーザーで絞り込み
    if (condition.userId) {
      andWhere.push({
        userId: {
          equals: condition.userId,
        },
      });
    }

    // 工程で絞り込み
    if (condition.formId) {
      andWhere.push({
        production: {
          is: {
            formId: {
              equals: condition.formId,
            },
          },
        },
      });
    }

    // ジャンルで絞り込み
    if (condition.genreId) {
      andWhere.push({
        production: {
          is: {
            productionGenres: {
              some: {
                genreId: {
                  equals: condition.genreId,
                },
              },
            },
          },
        },
      });
    }

    // シリーズで絞り込み
    if (condition.seriesId) {
      andWhere.push({
        production: {
          is: {
            productionSeries: {
              some: {
                seriesId: {
                  equals: condition.seriesId,
                },
              },
            },
          },
        },
      });
    }

    return {
      AND: andWhere,
    };
  }

  /**
   * 指定した検索条件に一致するお気に入りの件数を取得
   * @param condition
   * @returns
   */
  public aggregateSearch = async (condition: FavoriteProductionsSearchCondition) => {
    const client = useGraphQLClient();

    // 検索条件ビルド
    const where = this.buildSearchWhere(condition);

    try {
      const res = await client.aggregateFavoriteProductions({
        where,
      });
      return res.aggregateFavoriteProductions?.count;
    } catch (error) {
      console.log(error);
    }
    return null;
  };

  /**
   * 作品を検索
   * @param where
   */
  public search = async (condition: FavoriteProductionsSearchCondition) => {
    const client = useGraphQLClient();

    // 検索条件ビルド
    const where = this.buildSearchWhere(condition);

    try {
      const res = await client.findManyFavoriteProductions({
        where,
        take: condition.limit,
        skip: condition.offset,
        orderBy: condition.orderBy,
      });
      return res.findManyFavoriteProductions as FavoriteProductions[];
    } catch (error) {
      console.log(error);
    }
    return [];
  };

  /**
   * productionIdとuserIdを指定してお気に入りを取得します
   * @param id
   * @returns
   */
  public get = async (productionId: number, userId: number) => {
    const client = useGraphQLClient();
    try {
      const res = await client.findUniqueFavoriteProductions({
        where: {
          productionId_userId: {
            productionId,
            userId,
          },
        },
      });
      return res.findUniqueFavoriteProductions as FavoriteProductions;
    } catch (error) {
      console.log(error);
    }
    return undefined;
  }

  /**
   * 指定した検索条件に一致する作品の件数を取得
   * @param condition
   * @returns
   */
  public aggregateSerach = async (condition: FavoriteProductionsSearchCondition) => {
    const client = useGraphQLClient();

    // 検索条件ビルド
    const where = this.buildSearchWhere(condition);

    try {
      const res = await client.aggregateFavoriteProductions({
        where,
      });
      return res.aggregateFavoriteProductions?.count;
    } catch (error) {
      console.log(error);
    }
    return null;
  };

  /**
   * userIdとproductionIdを指定してお気に入りを登録します
   * @param data
   * @returns
   */
  public insert = async (data: any) => {
    const client = useGraphQLClient();
    try {
      // 挿入
      const input: FavoriteProductionsCreateInput = {
        user: {
          connect: {
            id: data.userId,
          },
        },
        production: {
          connect: {
            id: data.productionId,
          },
        },
      };

      // 作品を登録
      const res = await client.createOneFavoriteProductions({
        data: input,
      });

      return res.createOneFavoriteProductions as FavoriteProductions;
    } catch (error) {
      console.log(error);
    }
    return undefined;
  };

  /**
   * userIdとproductionIdを指定してお気に入りを削除します
   * @param data
   * @returns
   */
  public delete = async (data: any) => {
    const client = useGraphQLClient();
    try {
      // 挿入
      const input: FavoriteProductionsWhereUniqueInput = {
        productionId_userId: {
          userId: data.userId,
          productionId: data.productionId,
        },
      };

      // 作品を登録
      const res = await client.deleteOneFavoriteProductions({
        where: input,
      });

      return res.deleteOneFavoriteProductions;
    } catch (error) {
      console.log(error);
    }
    return null;
  }
}
export default new FavoriteService();
